/**
 * Readiness checklist + run-button messaging for community Configuration.
 */

export const COMMUNITY_READINESS_CHECK_IDS = {
    CODES: "codes",
    ECM_SYNC: "ecmSync",
    MEASURES: "measures",
    VALIDATION_BATCH: "validationBatch",
    CAPACITY: "capacity",
};

export const buildCommunityReadinessChecks = ({
    hasMissingCodes = false,
    ecmTableSyncIssues = [],
    measureStats = {},
    validationComboOutOfRange = false,
    insufficientRuns = false,
    validationInProgress = false,
}) => {
    const { measuresAllPassing = false, totalNonNone = 0 } = measureStats;
    const hasEcmSyncIssues = ecmTableSyncIssues.length > 0;

    return [
        {
            id: COMMUNITY_READINESS_CHECK_IDS.CODES,
            label: "Upgrade codes complete",
            status: hasMissingCodes ? "fail" : "pass",
            detail: hasMissingCodes ? "Re-select missing codes in the Upgrades tab and save your changes." : null,
        },
        {
            id: COMMUNITY_READINESS_CHECK_IDS.ECM_SYNC,
            label: "Upgrades match ECM map",
            status: hasEcmSyncIssues ? "fail" : "pass",
            detail: hasEcmSyncIssues
                ? `${ecmTableSyncIssues.length} upgrade row(s) are out of sync — fix them in Upgrades.`
                : null,
        },
        {
            id: COMMUNITY_READINESS_CHECK_IDS.MEASURES,
            label: "Measures validated",
            status:
                totalNonNone === 0
                    ? "pending"
                    : measuresAllPassing && !validationInProgress
                      ? "pass"
                      : validationInProgress
                        ? "pending"
                        : "fail",
            detail:
                totalNonNone === 0
                    ? "Add active measures in Upgrades, then run Validate upgrades."
                    : validationInProgress
                      ? "Validation is in progress."
                      : !measuresAllPassing
                        ? "Some measures failed or are not validated — use Validate upgrades on the Upgrades tab."
                        : null,
        },
        {
            id: COMMUNITY_READINESS_CHECK_IDS.VALIDATION_BATCH,
            label: "Validation batch size",
            status: validationComboOutOfRange ? "fail" : "pass",
            detail: validationComboOutOfRange
                ? "Too many or too few validation combinations — adjust active measures in Upgrades."
                : null,
        },
        {
            id: COMMUNITY_READINESS_CHECK_IDS.CAPACITY,
            label: "Run capacity",
            status: insufficientRuns ? "fail" : "pass",
            detail: insufficientRuns ? "Unlock more runs using Unlock Runs in the page header." : null,
        },
    ];
};

export const getReadinessSummary = (checks = []) => {
    const total = checks.length;
    const passing = checks.filter(({ status }) => status === "pass").length;
    const failing = checks.filter(({ status }) => status === "fail").length;
    return { total, passing, failing, allPassing: failing === 0 && checks.every(({ status }) => status !== "pending") };
};

/** Visual tone for run-button helper text (not every disabled state is an error). */
export const COMMUNITY_RUN_DISABLED_TONE = {
    INFO: "info",
    WARNING: "warning",
    ERROR: "error",
};

const runDisabledReason = (message, tone) => ({ message, tone });

export const getCommunityRunDisabledReason = ({
    isSimulationRunning = false,
    isPreparingRun = false,
    validationUiLocked = false,
    validationStarting = false,
    numToBeRun = 0,
    hasMissingCodes = false,
    ecmTableSyncIssues = [],
    measureStats = {},
    insufficientRuns = false,
    numUpgradePackages = 0,
} = {}) => {
    if (isSimulationRunning) {
        return runDisabledReason("A community simulation is already running.", COMMUNITY_RUN_DISABLED_TONE.INFO);
    }
    if (isPreparingRun) {
        return runDisabledReason("Preparing simulation run…", COMMUNITY_RUN_DISABLED_TONE.INFO);
    }
    if (validationUiLocked || validationStarting) {
        return runDisabledReason(
            "Wait for upgrade validation to finish (see Upgrades tab).",
            COMMUNITY_RUN_DISABLED_TONE.INFO
        );
    }
    if (hasMissingCodes) {
        return runDisabledReason(
            "Resolve missing upgrade codes on the Upgrades tab.",
            COMMUNITY_RUN_DISABLED_TONE.WARNING
        );
    }
    if (ecmTableSyncIssues.length > 0) {
        return runDisabledReason(
            "Fix upgrades that are out of sync with the ECM map on the Upgrades tab.",
            COMMUNITY_RUN_DISABLED_TONE.WARNING
        );
    }
    const { measuresAllPassing = false, totalNonNone = 0 } = measureStats;
    if (totalNonNone === 0) {
        return runDisabledReason("Add at least one active measure in Upgrades.", COMMUNITY_RUN_DISABLED_TONE.WARNING);
    }
    if (!measuresAllPassing) {
        return runDisabledReason(
            "All active measures must pass validation — run Validate upgrades on the Upgrades tab.",
            COMMUNITY_RUN_DISABLED_TONE.WARNING
        );
    }
    if (insufficientRuns) {
        return runDisabledReason(
            "Not enough unlocked runs for this community — use Unlock Runs in the page header.",
            COMMUNITY_RUN_DISABLED_TONE.ERROR
        );
    }
    if (numUpgradePackages < 1) {
        return runDisabledReason("Add at least one upgrade package on the Upgrades tab.", COMMUNITY_RUN_DISABLED_TONE.WARNING);
    }
    if (numToBeRun === 0) {
        return runDisabledReason("There are no new simulations to run.", COMMUNITY_RUN_DISABLED_TONE.INFO);
    }
    return null;
};
